// ./src/pages/private/EmbeddingsPage/EmbeddingsPage.tsx
import React, { useState, useCallback } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  EmbeddingFormModal,
  EmbeddingsTable,
  NeedAddVenue,
} from "@/components/features";
import { useEmbeddings } from "@/hooks";
import { Embedding } from "@/utils/api";
import { useApp, useVenuesContext } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const EmbeddingsPage: React.FC<PageProps> = (props: PageProps) => {
  const { isAuthenticated } = useApp();

  const { hasVenues } = useVenuesContext();

  // Use the embeddings hook for all data management
  const {
    // Data state
    embedding,
    embeddings,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingEmbeddingId,
    selectedEmbedding,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedEmbedding,

    // Error handling
    lastError,
    clearError,
  } = useEmbeddings({
    autoLoad: true,
    isAuthenticated,
    pageSize: 20,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [embeddingToDelete, setEmbeddingToDelete] = useState<Embedding | null>(
    null
  );
  const [searchValue, setSearchValue] = useState<string>("");

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const newFilters: any = {};
      if (value.trim()) {
        // Note: EmbeddingFilter doesn't have searchKeyword in the schema
        // You may need to add it or use a different filtering approach
        // For now, this is a placeholder
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list]
  );

  // Handle add embedding button click
  const handleAddEmbedding = useCallback(() => {
    setSelectedEmbedding(null);
    setFormModalOpened(true);
  }, [setSelectedEmbedding]);

  // Handle edit embedding
  const handleEditEmbedding = useCallback(
    (embedding: Embedding) => {
      setSelectedEmbedding(embedding);
      setFormModalOpened(true);
    },
    [setSelectedEmbedding]
  );

  // Handle duplicate embedding
  const handleDuplicateEmbedding = useCallback(
    (embedding: Embedding) => {
      setSelectedEmbedding({
        ...embedding,
        id: undefined,
        name: `${embedding.name} (Copy)`,
      });
      setFormModalOpened(true);
    },
    [setSelectedEmbedding]
  );

  // Handle view/preview embedding
  const handleViewEmbedding = useCallback((embedding: Embedding) => {
    if (embedding.id) {
      window.open(
        `/embedding/embeddings/${embedding.id}/preview`,
        "embedding-preview"
      );
    }
  }, []);

  // Handle remove embedding button click
  const handleRemoveEmbeddingClick = useCallback((embedding: Embedding) => {
    setEmbeddingToDelete(embedding);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (embeddingToDelete?.id) {
      const success = await remove(embeddingToDelete.id);
      if (success) {
        setEmbeddingToDelete(null);
      }
    }
  }, [embeddingToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setEmbeddingToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedEmbedding(null);
    clearError();
  }, [setSelectedEmbedding, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle embedding creation
  const handleCreateEmbedding = useCallback(
    async (embeddingData: any) => {
      const result = await create(embeddingData);
      if (result) {
        setFormModalOpened(false);
        setSelectedEmbedding(null);
      }
      return result;
    },
    [create, setSelectedEmbedding]
  );

  // Handle embedding update
  const handleUpdateEmbedding = useCallback(
    async (embeddingId: string, embeddingData: any) => {
      const result = await update(embeddingId, embeddingData);
      if (result) {
        setFormModalOpened(false);
        setSelectedEmbedding(null);
      }
      return result;
    },
    [update, setSelectedEmbedding]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-embedding"
                  iconName="Plus"
                  onClick={handleAddEmbedding}
                  disabled={isCreating || !!removingEmbeddingId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingEmbeddingId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search embeddings..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Embeddings table */}
        <EmbeddingsTable
          embeddings={embeddings}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditEmbedding={handleEditEmbedding}
          onDuplicateEmbedding={handleDuplicateEmbedding}
          onRemoveEmbedding={handleRemoveEmbeddingClick}
          onViewEmbedding={handleViewEmbedding}
          removingEmbeddingId={removingEmbeddingId}
          editedItemId={embedding?.id}
        />

        {/* Empty state when no embeddings */}
        <EmptyState
          display={isEmpty && !loading && hasVenues}
          title="No embeddings yet"
          description="Start by creating your first embedding to display events on external websites"
          actionIcon="Plus"
          actionLabel="Add Embedding"
          onAction={handleAddEmbedding}
        />

        {/* Empty state for no venue exist */}
        <NeedAddVenue
          display={!hasVenues && !loading}
          descriptionExtra="Then you can start adding embedding"
        />
      </Stack>

      {/* Embedding form modal for create/edit */}
      <EmbeddingFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        embedding={selectedEmbedding}
        onCreateEmbedding={handleCreateEmbedding}
        onUpdateEmbedding={handleUpdateEmbedding}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!embeddingToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Embedding"
        message={`Are you sure you want to delete "${embeddingToDelete?.name}"?`}
        details="This action cannot be undone. The embedding will be permanently removed and will no longer be accessible on external websites."
        confirmInProgress={removingEmbeddingId === embeddingToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
